import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import {
  fetchPendingCompanies,
  type PendingCompaniesListResponse,
} from '@/api/v4/projects.api';
import { QueryKey } from '@/config/query-client';

import { projectIdState } from '../project.state';
import { useAtomValue } from 'jotai';

interface UsePendingCompaniesParams {
  enabled?: boolean;
}

const ITEMS_PER_PAGE = 12;

export const usePendingCompanies = (params?: UsePendingCompaniesParams) => {
  const projectId = useAtomValue(projectIdState);
  const { enabled = true } = params || {};

  const query = useInfiniteQuery({
    queryKey: [QueryKey.PendingCompanies, projectId],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      if (!projectId) throw new Error('Project ID is required');

      return fetchPendingCompanies(projectId, {
        page: pageParam,
        items: ITEMS_PER_PAGE,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < ITEMS_PER_PAGE) {
        return undefined;
      }
      return allPages.length + 1;
    },
    enabled: Boolean(projectId) && enabled,
    retry: false,
    staleTime: 1000 * 60 * 2,
  });

  const pendingCompanies: PendingCompaniesListResponse[] = useMemo(() => {
    return query.data?.pages.flat() ?? [];
  }, [query.data]);

  return {
    pendingCompanies,
    ...query,
  };
};
